import { defineStore } from "pinia"
import { SoSelectObjectItem } from "@/types/components/SoSelect"
import { QueryResult } from "@/api/utils/types"
import { IAccount } from "@/models/account/account.types"
import { AccountService } from "@/services/AccountService"
import { RemoveReadonly } from "@/helpers/typeHelper"
import { searchAccounts } from "@/services-handlers/searchAccounts"

export type AccountSearchTerm =
  "email" | "telephone" | "surname" | "postcode" | "mpxn"

export type CustomerSearchTerm = "account" | AccountSearchTerm

export interface ICustomerSearchStore {
  query: string
  searchTerm: CustomerSearchTerm
  searchTerms: ISearchTerm[]
  limitReached: boolean
}

interface ISearchTerm extends SoSelectObjectItem {
  value: CustomerSearchTerm
  listResultsForSingleItem: boolean
}

export const useCustomerSearchStore = defineStore("customerSearch", {
  state: (): ICustomerSearchStore => {
    return {
      query: "",
      searchTerm: "account",
      limitReached: false,
      searchTerms: [
        {
          value: "account",
          title: "Account number",
          listResultsForSingleItem: false,
        },
        {
          value: "postcode",
          title: "Billing Postcode",
          listResultsForSingleItem: true,
        },
        { value: "email", title: "Email", listResultsForSingleItem: true },
        {
          value: "telephone",
          title: "Phone",
          listResultsForSingleItem: true,
        },
        {
          value: "surname",
          title: "Surname",
          listResultsForSingleItem: true,
        },
        {
          value: "mpxn",
          title: "MPXN",
          listResultsForSingleItem: false,
        },
      ],
    }
  },
  getters: {
    results: (state): QueryResult<IAccount[]> | null => {
      if (!state.searchTerm || !state.query) return null
      if (state.searchTerm === "account") {
        const query = AccountService.getAccount(state.query)
        return {
          ...query,
          data: query.data ? [query.data] : [],
        } as QueryResult<IAccount[]>
      } else {
        const searchResult = searchAccounts(state.searchTerm, state.query)

        if (searchResult.data) {
          state.limitReached = searchResult.data.limitReached
        }

        return {
          ...searchResult,
          data: searchResult.data?.accounts || [],
        } as unknown as QueryResult<IAccount[]>
      }
    },
  },
  actions: {
    search(searchTerm: CustomerSearchTerm, query: string) {
      const shouldResetLimit =
        this.searchTerm !== searchTerm || this.query !== query

      this.$patch({
        searchTerm,
        query,
        limitReached: shouldResetLimit ? false : this.limitReached,
      })
    },
  },
})

export type CustomerSearchTestStore = RemoveReadonly<
  ReturnType<typeof useCustomerSearchStore>
>
